import { useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useHostDay } from '../../../hooks/date/use-host-day'
import { useGetRocketsGamesLives } from '../../../hooks/queries/rockets-games-lives/use-get-rockets-games-lives'
import { useSub } from '../../../hooks/subscription/use-sub'
import type { RocketGameLive } from '../../../services/rockets-games-lives/types'
import { useGlobalStore } from '../../../store/global-store'

const LIVE_ROCKET_GAME = '/LIVE-ROCKET-GAME'

// payload que o serverless publica em /LIVE-ROCKET-GAME/{liveId}.
// IMPORTANTE: vem do pubsub DIRETO (não passa pelo case-mapper do gateway),
// então os valores de typeRocketGameProgress são snake ('in_exploded' / 'exploded').
interface RocketGameEvent {
  typeRocketGameProgress: string
  rocketGameLiveId: number
  rocketGameId: number
  rocketGameLevel?: number | null
  imgRocketGame: string
  liveId: number
  message: string
  dateInitialInExploded?: string
  dateFinalInExploded?: string
}

export const useLiveRocketGame = () => {
  const { liveId, liveUserId, utc } = useGlobalStore(
    useShallow((state) => ({
      liveId: state?.liveId,
      liveUserId: state?.liveUserId,
      utc: state?.utc,
    })),
  )

  // Dia no fuso do HOST (era o dia do device — errava a data quando o fuso do
  // espectador difere do da live) e re-computado na virada da meia-noite.
  const day = useHostDay(utc)

  const { rocketsGamesLives } = useGetRocketsGamesLives({
    take: 100,
    typeFilterDateContribute: 'equal',
    dateContributeInitial: day,
    dateContributeFinal: day,
    userLiveId: liveUserId!,
  })

  // bomba ativa vinda do broadcast — atualiza DIRETO da mensagem do server,
  // sem refetch (1 publish do server -> fan-out p/ todos os espectadores).
  const [bombFromEvent, setBombFromEvent] = useState<RocketGameLive | null>(null)
  // bombas que já estouraram (recebidas via broadcast) — filtra o fallback da query
  const explodedIdsRef = useRef<Set<number>>(new Set())

  useSub<RocketGameEvent>({
    endpoint: `${LIVE_ROCKET_GAME}/${liveId}`,
    skip: !liveId,
    onData: (payload) => {
      if (!payload) return

      const id = Number(payload.rocketGameLiveId)

      // nova bomba ativa (in_exploded) com a janela -> vira a bomba atual NA HORA
      if (
        payload.typeRocketGameProgress === 'in_exploded' &&
        payload.dateFinalInExploded
      ) {
        explodedIdsRef.current.delete(id)

        setBombFromEvent({
          id,
          rocketGameId: Number(payload.rocketGameId),
          typeRocketGameProgress: 'inExploded',
          dateInitialInExploded: payload.dateInitialInExploded,
          dateFinalInExploded: payload.dateFinalInExploded,
          rocketGame: {
            imgRocketGame: payload.imgRocketGame,
            level: Number(payload.rocketGameLevel ?? 0),
          },
        } as unknown as RocketGameLive)

        return
      }

      // bomba estourou (exploded) -> se for a atual, some; a próxima chega noutro
      // broadcast (in_exploded). Sem refetch.
      if (payload.typeRocketGameProgress === 'exploded') {
        explodedIdsRef.current.add(id)
        setBombFromEvent((current) =>
          current && current.id === id ? null : current,
        )
      }
    },
  })

  const fallbackBomb = rocketsGamesLives?.find(
    (rocket) =>
      rocket?.typeRocketGameProgress !== 'exploded' &&
      !explodedIdsRef.current.has(Number(rocket?.id)),
  )

  const currentRocketGameLive = bombFromEvent ?? fallbackBomb

  return {
    currentRocketGameLive,
  }
}
